import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputText } from './Inputs/InputText';
import { InputPostalCode } from './Inputs/InputPostalCode';
import { InputPhone } from './Inputs/InputPhone';
import Button from './core/Button';
import { IconCheck } from '@/icons';
import { useEffect, useMemo, useState } from 'react';
import {
  submitOrganizationApplicationBody,
  useSubmitOrganizationApplication,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import CheckBox from '@/components/core/Checkbox.tsx';
import { useNavigate } from '@tanstack/react-router';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { useTenantID } from '@/hooks/useTenantID';
import {
  EINVOICE_EMAIL_MAX_LENGTH,
  normalizeGlnInput,
  optionalTrimmedText,
  validateInvoicingIdentifiers,
} from '@/helpers/invoicingIdentifiers';
import { trimData } from '@/helpers/trimData';
import { useBranding } from '@/hooks/useBranding';

export const ApplyForAccountForm = () => {
  const tenantId = useTenantID();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { serviceName } = useBranding();

  const createOrganizationApplication = useSubmitOrganizationApplication();

  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

  const isLTR = tenantId.toLowerCase() === 'ltr';

  type OrganizationType = z.infer<typeof submitOrganizationApplicationBody>;

  const schema = z
    .preprocess((data) => {
      const values = data as OrganizationType;
      return {
        ...values,
        gln: optionalTrimmedText(values.gln),
        einvoice_email: optionalTrimmedText(values.einvoice_email),
      };
    }, submitOrganizationApplicationBody)
    .superRefine((data, ctx) => {
      validateInvoicingIdentifiers({
        values: data,
        ctx,
        formatMessage,
      });
    });

  const { handleSubmit, control, formState, watch, setValue } =
    useForm<OrganizationType>({
      resolver: zodResolver(schema, {
        errorMap: useCustomErrorMap(),
      }),
      mode: 'all',
      defaultValues: {
        tenant_id: tenantId,
        contact_phone: '+46',
      },
    });

  const organizationNumber = watch('organization_number');

  useEffect(() => {
    if (!organizationNumber) return;

    if (organizationNumber.includes(' ')) {
      setValue('organization_number', organizationNumber.replace(' ', ''));
      return;
    }

    if (organizationNumber.includes('-')) return;

    if (organizationNumber.length > 6) {
      const formatted = `${organizationNumber.slice(0, 6)}-${organizationNumber.slice(6)}`;
      setValue('organization_number', formatted);
    }
  }, [organizationNumber, setValue]);

  const onSubmit: SubmitHandler<OrganizationType> = (data) => {
    // Handle all optional contact fields that might have empty strings
    const submitData: OrganizationType = JSON.parse(
      JSON.stringify(trimData(data)),
    );

    createOrganizationApplication.mutate(
      { data: submitData },
      {
        onSuccess: () => {
          toast.success(
            {
              title: `Ansökan för ${data.name} med organsiationsnummer ${data.organization_number} är skickad.`,
            },
            { autoClose: 4000 },
          );
          navigate({ to: '/$tenantId/login', params: { tenantId } });
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  };

  const phone = watch('contact_phone');
  const phoneError = useMemo(() => {
    if (!phone) return '';
    if (phone.startsWith('+46')) return '';
    return formatMessage({
      defaultMessage: 'Telefonnummer måste börja med +46',
    });
  }, [formatMessage, phone]);

  const companyDetails = () => {
    return (
      <div className="flex flex-col gap-10">
        <h2 className="font-medium">
          <FormattedMessage defaultMessage="Företagsuppgifter" />
        </h2>
        <div className="flex flex-col gap-10 md:flex-row">
          <div className="flex-auto">
            <InputText
              label="Organisationsnamn"
              name="name"
              control={control}
              editMode={true}
              error={formState.errors.name}
              helperText="Se till att inkludera enhet/avdelning i namnet om relevant"
              helperTextReserveSpace={true}
              required
            />
          </div>
          <div className="flex-auto">
            <InputText
              label="Organisationsnummer"
              name="organization_number"
              placeholder="123456-1234"
              control={control}
              editMode={true}
              error={formState.errors.organization_number}
              required
            />
          </div>
        </div>
      </div>
    );
  };

  const invoiceDetails = () => {
    return (
      <div className="mt-5 flex flex-col gap-10">
        <h2 className="text-2xl font-medium">
          <FormattedMessage defaultMessage="Faktureringsuppgifter" />
        </h2>

        <InputText
          label="Adress"
          name="invoicing_address.street"
          control={control}
          editMode={true}
          error={formState.errors.invoicing_address?.street}
          required
        />

        <div className="flex flex-col gap-10 md:flex-row">
          <InputPostalCode
            label="Postnummer"
            name="invoicing_address.zip_code"
            control={control}
            editMode={true}
            error={formState.errors.invoicing_address?.zip_code}
            required
          />
          <InputText
            label="Postort"
            name="invoicing_address.city"
            control={control}
            editMode={true}
            error={formState.errors.invoicing_address?.city}
            required
          />
        </div>
        <InputText
          label="Er referens"
          name="invoicing_reference"
          control={control}
          editMode={true}
          error={formState.errors.invoicing_reference}
          required
        />
        {isLTR && (
          <InputText
            label="Kostnadsställe"
            name="suggested_cost_center"
            helperText="Kostnadsställe är obligatoriskt för kunder inom Region Jämtland Härjedalen."
            helperTextReserveSpace={true}
            control={control}
            editMode={true}
            error={formState.errors.suggested_cost_center}
          />
        )}
        <div className="flex flex-col gap-10 md:flex-row">
          <InputText
            label={formatMessage({ defaultMessage: 'GLN-nummer' })}
            name="gln"
            type="number"
            normalize={normalizeGlnInput}
            control={control}
            editMode={true}
            error={formState.errors.gln}
          />
          <InputText
            label={formatMessage({ defaultMessage: 'E-fakturaadress' })}
            name="einvoice_email"
            placeholder="faktura@exempel.se"
            control={control}
            editMode={true}
            error={formState.errors.einvoice_email}
            maxLength={EINVOICE_EMAIL_MAX_LENGTH}
          />
        </div>
      </div>
    );
  };

  const deliveryDetails = () => {
    return (
      <div className="mt-5 flex flex-col gap-10">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-medium">
            <FormattedMessage defaultMessage="Leveransadress" />
          </h2>
          <p className="text-text-700">
            <FormattedMessage defaultMessage="Leveransadressen används för att skicka ut fysiska kort om det beställs." />
          </p>
          <Button
            onClick={() => {
              setValue(
                'delivery_address.street',
                watch('invoicing_address.street'),
                { shouldDirty: true },
              );
              setValue(
                'delivery_address.zip_code',
                watch('invoicing_address.zip_code'),
                { shouldDirty: true },
              );
              setValue(
                'delivery_address.city',
                watch('invoicing_address.city'),
                { shouldDirty: true },
              );
            }}
          >
            <FormattedMessage defaultMessage="Kopiera från faktureringsadress" />
          </Button>
        </div>

        <InputText
          label="Adress"
          name="delivery_address.street"
          control={control}
          editMode={true}
          error={formState.errors.delivery_address?.street}
          required
        />
        <div className="flex flex-col gap-10 md:flex-row">
          <InputPostalCode
            label="Postnummer"
            name="delivery_address.zip_code"
            control={control}
            editMode={true}
            error={formState.errors.delivery_address?.zip_code}
            required
          />
          <InputText
            label="Postort"
            name="delivery_address.city"
            control={control}
            editMode={true}
            error={formState.errors.delivery_address?.city}
            required
          />
        </div>
      </div>
    );
  };

  const contactDetails = () => {
    return (
      <div className="mt-3 flex flex-col gap-10">
        <h2 className="text-2xl font-medium">
          <FormattedMessage defaultMessage="Kontaktperson" />
        </h2>
        <div className="flex w-full flex-col gap-10 md:flex-row">
          <InputText
            className="w-full"
            label="Förnamn"
            name="contact_first_name"
            control={control}
            editMode={true}
            error={formState.errors.contact_first_name}
            required
          />

          <InputText
            label="Efternamn"
            name="contact_last_name"
            control={control}
            editMode={true}
            error={formState.errors.contact_last_name}
            required
          />
        </div>

        <InputText
          label="E-postadress"
          name="contact_email"
          control={control}
          editMode={true}
          error={formState.errors.contact_email}
          required
        />

        <InputPhone
          label="Telefon"
          name="contact_phone"
          control={control}
          editMode={true}
          placeholder="+46"
          error={
            formState.errors.contact_phone || {
              message: phoneError,
              type: 'custom',
            }
          }
          required
        />
      </div>
    );
  };

  return (
    <div className="mx-auto w-full rounded bg-background-50">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex h-full flex-col gap-4 rounded-md border border-background-400 p-4 md:p-8">
          <h1 className="text-4xl font-medium">
            <FormattedMessage defaultMessage="Ansök om konto" />
          </h1>
          {companyDetails()}
          {invoiceDetails()}
          {deliveryDetails()}
          {contactDetails()}
          <div className="mb-3 mt-10">
            <CheckBox
              label={formatMessage(
                {
                  defaultMessage:
                    'Jag intygar att jag för företagets räkning har mandat/är behörig att ansöka om konto i {serviceName}',
                },
                {
                  serviceName: serviceName,
                },
              )}
              id="isAuthorizedCheckbox"
              checked={isAuthorized}
              onCheckedChange={(state) => setIsAuthorized(!!state)}
            />
          </div>
          <div className="flex w-full flex-wrap justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() =>
                navigate({ to: '/$tenantId/login', params: { tenantId } })
              }
            >
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              startIcon={<IconCheck />}
              type="submit"
              variant="filled"
              disabled={!formState.isValid || !isAuthorized || !!phoneError}
              loading={createOrganizationApplication.isPending}
            >
              <FormattedMessage defaultMessage="Skicka ansökan" />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
